import { lazy } from "react";

import AuthLayout from "@/components/auth/AuthLayout";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import RootRedirect from "@/components/auth/RootRedirect";
// Business dashboard components
import BusinessAnalytics from "@/components/business/Analytics";
import BusinessDashboard from "@/components/business/Dashboard";
import BusinessDocuments from "@/components/business/Documents";
import BusinessManagement from "@/components/business/Management";
import BusinessProfile from "@/components/business/Profile";
import Analytics from "@/components/drivers/Analytics";
// Driver dashboard components
import Dashboard from "@/components/drivers/Dashboard";
import Documents from "@/components/drivers/Documents";
import Profile from "@/components/drivers/Profile";
import BusinessLayout from "@/layouts/BusinessLayout";
import DriverLayout from "@/layouts/DriverLayout";
import MasterLayout from "@/layouts/MasterLayout";
// About pages
import AirportServices from "@/pages/aboutUs/AirportServices";
import Careers from "@/pages/aboutUs/Careers";
import Cities from "@/pages/aboutUs/CitiesWeServe";
import ForCommunities from "@/pages/aboutUs/Forcommunities";
import ForInvestors from "@/pages/aboutUs/ForInvestors";
import Updates from "@/pages/aboutUs/Updates";
import WhoWeAre from "@/pages/aboutUs/WhoWeAre";
import DriverRegisterPage from "@/pages/auth/DriverRegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
// Auth pages
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import BecomeCourierPage from "@/pages/BecomeCourierPage";
import BecomeDeliveryDriver from "@/pages/BecomeDeliveryDriver";
import BecomeDriver from "@/pages/BecomeDriver";
import BecomeScooterDriver from "@/pages/BecomeScooterDriver";
import CourierApplicationPage from "@/pages/business/CourierApplicationPage";
import FleetApplicationPage from "@/pages/business/FleetApplicationPage";
import RestaurantApplicationPage from "@/pages/business/RestaurantApplicationPage";
import CityRequest from "@/pages/CityRequest";
import ComingSoon from "@/pages/ComingSoon";
import ContactUs from "@/pages/ContactUs";
import Eats from "@/pages/Eats";
import EmergencyReport from "@/pages/EmergencyReport";
import FAQ from "@/pages/FAQ";
import CookiePreferences from "@/pages/legal/CookiePreferences";
import DataProtection from "@/pages/legal/DataProtection";
import PrivacyPolicy from "@/pages/legal/PrivacyPolicy";
import TermsOfUse from "@/pages/legal/TermsOfUse";
import Maintenance from "@/pages/Maintenance";
import RegisterFleet from "@/pages/RegisterFleet";
import RegisterRestaurant from "@/pages/RegisterRestaurant";
import ReportIssue from "@/pages/ReportIssue";
import ReportVehicle from "@/pages/ReportVehicle";
import Restaurants from "@/pages/Restaurants";
import RiderSafety from "@/pages/RiderSafety";
import Rides from "@/pages/Rides";
import Scooters from "@/pages/Scooters";
import ScooterSafety from "@/pages/ScooterSafety";
import Page404 from "@/pages/Status/Page404";
import UpdateDetailPage from "@/pages/UpdateDetailPage";
import { type AppRouteObject } from "@/router/interface";
// Lazy load the routes to improve performance
const Home = lazy(() => import("@/pages/Home"));

const routes: AppRouteObject[] = [
  // Root redirect - check auth and redirect appropriately
  {
    element: <RootRedirect />,
    path: "/",
  },
  // Home page with MasterLayout (includes all sections)
  {
    children: [
      {
        element: <Home />,
        index: true,
      },
    ],
    element: <MasterLayout />,
    path: "/home",
  },
  // Authentication routes with AuthLayout
  {
    children: [
      {
        element: (
          <ProtectedRoute requireAuth={false}>
            <LoginPage />
          </ProtectedRoute>
        ),
        path: "login",
      },
      {
        element: (
          <ProtectedRoute requireAuth={false}>
            <RegisterPage />
          </ProtectedRoute>
        ),
        path: "register",
      },
      {
        element: (
          <ProtectedRoute requireAuth={false}>
            <DriverRegisterPage />
          </ProtectedRoute>
        ),
        path: "driver-register",
      },
      {
        element: (
          <ProtectedRoute requireAuth={false}>
            <ForgotPasswordPage />
          </ProtectedRoute>
        ),
        path: "forgot-password",
      },
      {
        element: (
          <ProtectedRoute requireAuth={false}>
            <ResetPasswordPage />
          </ProtectedRoute>
        ),
        path: "reset-password",
      },
    ],
    element: <AuthLayout />,
    path: "/auth",
  },
  // Driver dashboard routes with DriverLayout
  {
    children: [
      {
        element: (
          <ProtectedRoute requireAuth={true}>
            <Dashboard />
          </ProtectedRoute>
        ),
        path: "dashboard",
      },
      {
        element: (
          <ProtectedRoute requireAuth={true}>
            <Documents />
          </ProtectedRoute>
        ),
        path: "documents",
      },
      {
        element: (
          <ProtectedRoute requireAuth={true}>
            <Analytics />
          </ProtectedRoute>
        ),
        path: "analytics",
      },
      {
        element: (
          <ProtectedRoute requireAuth={true}>
            <Profile />
          </ProtectedRoute>
        ),
        path: "profile",
      },
    ],
    element: <DriverLayout />,
    path: "/driver",
  },
  // Business dashboard routes with BusinessLayout
  {
    children: [
      {
        element: (
          <ProtectedRoute requireAuth={true}>
            <BusinessDashboard />
          </ProtectedRoute>
        ),
        path: "dashboard",
      },
      {
        element: (
          <ProtectedRoute requireAuth={true}>
            <BusinessAnalytics />
          </ProtectedRoute>
        ),
        path: "analytics",
      },
      {
        element: (
          <ProtectedRoute requireAuth={true}>
            <BusinessProfile />
          </ProtectedRoute>
        ),
        path: "profile",
      },
      {
        element: (
          <ProtectedRoute requireAuth={true}>
            <BusinessManagement />
          </ProtectedRoute>
        ),
        path: "management",
      },
      {
        element: (
          <ProtectedRoute requireAuth={true}>
            <BusinessDocuments />
          </ProtectedRoute>
        ),
        path: "documents",
      },
    ],
    element: <BusinessLayout />,
    path: "/business",
  },
  // Public pages without specific layout (they can have their own internal layouts)
  {
    element: <BecomeCourierPage />,
    path: "/partners/courier-registration",
  },
  {
    element: <RegisterRestaurant />,
    path: "/partners/restaurant-registration",
  },
  {
    element: <RegisterFleet />,
    path: "/partners/fleet-registration",
  },
  // Business application pages
  {
    element: <RestaurantApplicationPage />,
    path: "/business/apply/restaurant",
  },
  {
    element: <FleetApplicationPage />,
    path: "/business/apply/fleet",
  },
  {
    element: <CourierApplicationPage />,
    path: "/business/apply/courier",
  },
  // Business application pages
  {
    element: <RestaurantApplicationPage />,
    path: "/business/apply/restaurant",
  },
  {
    element: <FleetApplicationPage />,
    path: "/business/apply/fleet",
  },
  {
    element: <CourierApplicationPage />,
    path: "/business/apply/courier",
  },
  {
    element: <RiderSafety />,
    path: "/safety/rider-safety",
  },
  {
    element: <ScooterSafety />,
    path: "/safety/scooter-safety",
  },
  {
    element: <BecomeDriver />,
    path: "/driver/vehicle-driver-signup",
  },
  {
    element: <BecomeScooterDriver />,
    path: "/driver/scooter-driver-signup",
  },
  {
    element: <ReportVehicle />,
    path: "/support/report-vehicle",
  },
  {
    element: <FAQ />,
    path: "/support/faq",
  },
  {
    element: <EmergencyReport />,
    path: "/support/emergency-report",
  },
  {
    element: <ReportIssue />,
    path: "/support/report-issue",
  },
  {
    element: <BecomeDeliveryDriver />,
    path: "/driver/delivery-signup",
  },
  {
    element: <Rides />,
    path: "/pages/catch-rides",
  },
  {
    element: <Scooters />,
    path: "/pages/find-scooters",
  },
  {
    element: <Restaurants />,
    path: "/pages/restaurants",
  },
  {
    element: <Eats />,
    path: "/pages/eats",
  },
  {
    element: <WhoWeAre />,
    path: "/pages/about-us/who-we-are",
  },
  {
    element: <Careers />,
    path: "/pages/about-us/careers",
  },
  {
    element: <Updates />,
    path: "/pages/about-us/updates",
  },
  {
    element: <UpdateDetailPage />,
    path: "/updates/:id",
  },
  {
    element: <ForInvestors />,
    path: "/pages/about-us/for-investors",
  },
  {
    element: <ForCommunities />,
    path: "/pages/about-us/for-communities",
  },
  {
    element: <Cities />,
    path: "/pages/about-us/cities-we-serve",
  },
  {
    element: <AirportServices />,
    path: "/pages/about-us/airport-services",
  },
  {
    element: <TermsOfUse />,
    path: "/pages/legal/terms-of-use",
  },
  {
    element: <PrivacyPolicy />,
    path: "/pages/legal/privacy-policy",
  },
  {
    element: <CookiePreferences />,
    path: "/pages/legal/cookie-preferences",
  },
  {
    element: <DataProtection />,
    path: "/pages/legal/data-protection",
  },
  {
    element: <ComingSoon />,
    path: "/coming-soon",
  },
  {
    element: <Maintenance />,
    path: "/maintenance",
  },
  {
    element: <ContactUs />,
    path: "/contact-us",
  },
  {
    element: <CityRequest />,
    path: "/pages/city-request",
  },
  {
    element: <Page404 />,
    path: "*",
  },
];

export default routes;
